import { useEffect, useRef, useState, useMemo } from "react";
import { allowOrWarn, Util } from '../../util/Util';
import Invoices from "./Invoices";
import { useAuth } from "../../hooks/useAuth";
import { useData } from "../../hooks/useData";
import { useUI } from "../../providers/MoleUIContext";
import { toast } from "react-toastify";
import BulkPaymentModal from "../payments/BulkPaymentModal";
import { Box, Button, IconButton, ListItemIcon, MenuItem, Tooltip, Typography, useTheme } from '@mui/material';
import { LocalStorageKeys, NUMBER_FILTER_OPTIONS, TEXT_FILTER_OPTIONS } from "../../util/constants";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import TableView from "../../components/TableView";
import { AttachMoney, FileDownload, Refresh } from "@mui/icons-material";
import { useDateRange, getPeriodQueryParams } from "../../hooks/useDateRange";
import SituationExportDialog, { PageOrientation, SituationDebtExportOptions } from "../../components/SituationExportDialog";
import { MRT_ColumnDef } from "material-react-table";
import {
	RECON_CHART_COLORS,
	ReconPhase,
	ReconRow,
	buildWaterfallSteps,
	CreditUsageCell,
	creditUsageTableCellProps,
	getChartSurface,
	isUnlimitedCredit,
	SituationBalanceFlowChart,
	SituationChartsGrid,
	SituationDetailHeader,
	SituationDetailShell,
	SituationPeriodAlert,
	SituationEndBalancesChart,
	SituationEntityComposedChart,
	SituationKpiGrid,
	SituationReconciliation,
} from './situationDetailShared';

import { roundQty } from "../../util/quantity";
import { canRecord } from '../../util/access';
const hasPeriodActivity = (record: any) => [
	'period_agent_invoiced', 'period_agent_paid', 'period_agent_balance',
	'period_client_invoiced', 'period_client_paid', 'period_client_balance',
	'period_overlap_invoiced', 'period_overlap_paid', 'period_overlap_balance',
	'period_total_invoiced', 'period_total_paid', 'period_total_balance',
].some((key) => Number(record[key] ?? 0) !== 0);

const ADJUSTED_ROW_LABEL = 'Adjusted';

const ENTITY_KEYS: Record<string, string> = {
	Agent: 'agent',
	Client: 'client',
	Overlap: 'overlap',
	[ADJUSTED_ROW_LABEL]: 'adjusted',
};

const AgentSituation = () => {
	const [toPay, setToPay] = useState<any>(null);
	const [toView, setToView] = useState<any>(null);
	const [onExport, setOnExport] = useState<boolean>(false);
	const [exportSelectedIds, setExportSelectedIds] = useState<number[]>([]);

	const theme = useTheme();
	const { api, session, access } = useAuth();
	const { setLoadingMessage, queueReRun, t } = useUI();
	const { dateRange: period, setDateRange: setPeriod } = useDateRange(LocalStorageKeys.AGENT_SIT_RECORDS);
	const { data, lastUpdate, loadFromIndexedDB, saveFromServer, dateRange, loading, setLoading } = useData(LocalStorageKeys.AGENT_SIT_RECORDS);

	const toastIdRef = useRef<string | number>(-1);
	const firstFetchRef = useRef(false);


	// useEffect(() => {
	// 	return () => {
	// 		// Runs just before the component is destroyed
	// 		if (paymentMade.current) {
	// 			onCallback?.();
	// 		}
	// 	};
	// }, []);

	useEffect(() => {
		if (!loadFromIndexedDB() && !firstFetchRef.current) {
			fetchData();
			firstFetchRef.current = true;
		}
	}, []);

	const fetchData = async (useLoadModal: boolean = true) => {

		setLoading(true);

		const res = await api.get("clients/situations", {
			params: {
				agentsOnly: true,
				...getPeriodQueryParams(period),
			}
		});

		if (res) {
			if (res.status === 200) {
				saveFromServer(res.data, period);
				toast.update(toastIdRef.current, {
					render: t("common:toast.success.refresh"),
					type: "success",
					isLoading: false,
					autoClose: 3000,
				});
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchData);
			} else {
				console.error("Failed to fetch data:", res);
				toast.dismiss(toastIdRef.current);
			}
		} else {
			toast.dismiss(toastIdRef.current);
		}

		setLoading(false);
	};

	const openPdfBlob = (encoded: string) => {
		const pdfData = atob(encoded);
		const blob = new Blob([Uint8Array.from(pdfData, (c) => c.charCodeAt(0))], { type: "application/pdf" });
		window.open(URL.createObjectURL(blob));
	};

	const exportLedger = async (selectedIds: number[], orientation: PageOrientation = "landscape") => {
		setLoadingMessage("EXPORT");

		const payload: Record<string, unknown> = {
			...getPeriodQueryParams(period),
			orientation,
		};
		if (selectedIds.length > 0) {
			payload.Agents = selectedIds;
		}

		const res = await api.post("/reports/invoices/agents/ledger", payload);

		if (res?.status === 200) {
			openPdfBlob(res.data.data);
		} else if (res) {
			console.error("Failed to export ledger:", res);
		}

		setLoadingMessage(null);
	};

	const exportDebt = async (selectedIds: number[], options: SituationDebtExportOptions) => {
		setLoadingMessage("EXPORT");

		const payload: Record<string, unknown> = {
			...getPeriodQueryParams(period),
			agentsOnly: true,
			includeZeroBalance: options.includeZeroBalance,
			includeSuspended: options.includeSuspended,
			comprehensive: options.comprehensive,
			fileType: options.fileType,
		};
		if (selectedIds.length > 0) {
			payload.Agents = selectedIds;
		}

		const res = await api.post("print/debts", payload);

		if (res?.status === 200) {
			if (options.fileType === "pdf") {
				openPdfBlob(res.data.data);
			} else {
				const blob = new Blob([res.data], { type: "text/csv" });
				const url = window.URL.createObjectURL(blob);
				const a = document.createElement("a");
				a.href = url;
				const { From, To } = getPeriodQueryParams(period);
				a.download = From && To ? `agent_debts_${From}-${To}.csv` : `agent_debts_current.csv`;
				document.body.appendChild(a);
				a.click();
				a.remove();
				window.URL.revokeObjectURL(url);
			}
		} else if (res) {
			console.error("Failed to export debt situation:", res);
		}

		setLoadingMessage(null);
	};

	const renderAgentDetailPanel = ({ row }: { row: any }) => {
		const record = row.original;
		const chartSurface = getChartSurface(theme);
		const entityLabel = (label: string) =>
			t(`common:clientSituation.detailPanel.entities.${ENTITY_KEYS[label] ?? label.toLowerCase()}`);
		const phaseLabel = (phase: ReconPhase) => t(`common:clientSituation.detailPanel.phases.${phase}`);
		const periodActive = hasPeriodActivity(record);
		const unlimited = isUnlimitedCredit(Number(record.credit_limit ?? 0));
		const isActive = record.status;

		// Build grouped rows: Agent, Client, Overlap, Adjusted (Agent + Client − Overlap)
		const agentBeforeInvoiced = Number(record.before_agent_invoiced ?? 0);
		const agentBeforePaid = Number(record.before_agent_paid ?? 0);
		const agentBeforeBalance = Number(record.before_agent_balance ?? 0);

		const clientBeforeInvoiced = Number(record.before_client_invoiced ?? 0);
		const clientBeforePaid = Number(record.before_client_paid ?? 0);
		const clientBeforeBalance = Number(record.before_client_balance ?? 0);

		const overlapBeforeInvoiced = Number(record.before_overlap_invoiced ?? 0);
		const overlapBeforePaid = Number(record.before_overlap_paid ?? 0);
		const overlapBeforeBalance = Number(record.before_overlap_balance ?? 0);

		const totalsBeforeInvoiced = Number(record.before_total_invoiced ?? 0);
		const totalsBeforePaid = Number(record.before_total_paid ?? 0);
		const totalsBeforeBalance = Number(record.before_total_balance ?? 0);

		const agentPeriodInvoiced = Number(record.period_agent_invoiced ?? 0);
		const agentPeriodPaid = Number(record.period_agent_paid ?? 0);
		const agentPeriodBalance = Number(record.period_agent_balance ?? 0);

		const clientPeriodInvoiced = Number(record.period_client_invoiced ?? 0);
		const clientPeriodPaid = Number(record.period_client_paid ?? 0);
		const clientPeriodBalance = Number(record.period_client_balance ?? 0);

		const overlapPeriodInvoiced = Number(record.period_overlap_invoiced ?? 0);
		const overlapPeriodPaid = Number(record.period_overlap_paid ?? 0);
		const overlapPeriodBalance = Number(record.period_overlap_balance ?? 0);

		const totalsPeriodInvoiced = Number(record.period_total_invoiced ?? 0);
		const totalsPeriodPaid = Number(record.period_total_paid ?? 0);
		const totalsPeriodBalance = Number(record.period_total_balance ?? 0);

		const agentAfterInvoice = Number(record.after_agent_invoiced ?? 0);
		const agentAfterPaid = Number(record.after_agent_paid ?? 0);
		const agentAfterBalance = Number(record.after_agent_balance ?? 0);

		const clientAfterInvoice = Number(record.after_client_invoiced ?? 0);
		const clientAfterPaid = Number(record.after_client_paid ?? 0);
		const clientAfterBalance = Number(record.after_client_balance ?? 0);

		const overlapAfterInvoice = Number(record.after_overlap_invoiced ?? 0);
		const overlapAfterPaid = Number(record.after_overlap_paid ?? 0);
		const overlapAfterBalance = Number(record.after_overlap_balance ?? 0);

		const totalsAfterInvoiced = Number(record.after_total_invoiced ?? 0);
		const totalsAfterPaid = Number(record.after_total_paid ?? 0);
		const totalsAfterBalance = Number(record.after_total_balance ?? 0);

		const rows = [
			{
				label: 'Agent',
				before: { invoiced: agentBeforeInvoiced, paid: agentBeforePaid, balance: agentBeforeBalance },
				period: { invoiced: agentPeriodInvoiced, paid: agentPeriodPaid, balance: agentPeriodBalance },
				end: { invoiced: agentAfterInvoice, paid: agentAfterPaid, balance: agentAfterBalance },
			},
			{
				label: 'Client',
				before: { invoiced: clientBeforeInvoiced, paid: clientBeforePaid, balance: clientBeforeBalance },
				period: { invoiced: clientPeriodInvoiced, paid: clientPeriodPaid, balance: clientPeriodBalance },
				end: { invoiced: clientAfterInvoice, paid: clientAfterPaid, balance: clientAfterBalance },
			},
			{
				label: 'Overlap',
				before: { invoiced: overlapBeforeInvoiced, paid: overlapBeforePaid, balance: overlapBeforeBalance },
				period: { invoiced: overlapPeriodInvoiced, paid: overlapPeriodPaid, balance: overlapPeriodBalance },
				end: { invoiced: overlapAfterInvoice, paid: overlapAfterPaid, balance: overlapAfterBalance },
			},
			{
				label: ADJUSTED_ROW_LABEL,
				before: { invoiced: totalsBeforeInvoiced, paid: totalsBeforePaid, balance: totalsBeforeBalance },
				period: { invoiced: totalsPeriodInvoiced, paid: totalsPeriodPaid, balance: totalsPeriodBalance },
				end: { invoiced: totalsAfterInvoiced, paid: totalsAfterPaid, balance: totalsAfterBalance },
			},
		];

		const entityRows = rows.filter((rowItem) => rowItem.label !== ADJUSTED_ROW_LABEL);
		const adjustedRow = rows.find((rowItem) => rowItem.label === ADJUSTED_ROW_LABEL)!;

		const buildEntityChartData = (phase: 'end' | 'period') => [
			...entityRows.map((rowItem) => ({
				entity: entityLabel(rowItem.label),
				invoiced: rowItem[phase].invoiced,
				paid: rowItem[phase].paid,
				balance: rowItem[phase].balance,
			})),
			{
				entity: entityLabel(ADJUSTED_ROW_LABEL),
				invoiced: adjustedRow[phase].invoiced,
				paid: adjustedRow[phase].paid,
				balance: adjustedRow[phase].balance,
			},
		];

		const endEntityChartData = buildEntityChartData('end');
		const periodEntityChartData = buildEntityChartData('period');

		const endBalanceChartData = [
			...entityRows.map((rowItem) => ({
				entity: entityLabel(rowItem.label),
				balance: rowItem.end.balance,
				fill: rowItem.end.balance < 0
					? chartSurface.creditBalance
					: RECON_CHART_COLORS[ENTITY_KEYS[rowItem.label] as 'agent' | 'client' | 'overlap'],
			})),
			{
				entity: entityLabel(ADJUSTED_ROW_LABEL),
				balance: adjustedRow.end.balance,
				fill: theme.palette.primary.main,
			},
		];

		const waterfallData = buildWaterfallSteps(adjustedRow, {
			balance: RECON_CHART_COLORS.balance,
			increase: chartSurface.debitBalance,
			decrease: chartSurface.creditBalance,
		}).map((step, index) => ({
			...step,
			name: (['before', 'period', 'end'] as ReconPhase[]).map(phaseLabel)[index],
		}));

		const adjustedEntityLabel = entityLabel(ADJUSTED_ROW_LABEL);

		return (
			<SituationDetailShell theme={theme}>
				<SituationDetailHeader
					name={record.name}
					subtitle={record.is_agent ? t('common:clientSituation.detailPanel.agent') : t('common:clientSituation.detailPanel.client')}
					isActive={isActive}
					unlimited={unlimited}
					creditLimit={record.credit_limit}
					t={t}
				/>

				<SituationPeriodAlert
					periodActive={periodActive}
					label={t('common:clientSituation.detailPanel.noPeriodActivity')}
				/>

				<SituationKpiGrid
					theme={theme}
					stats={[
						{ label: t('common:clientSituation.detailPanel.endBalance'), value: totalsAfterBalance, signed: true },
						{ label: t('common:clientSituation.detailPanel.endInvoiced'), value: totalsAfterInvoiced, signed: false },
						{ label: t('common:clientSituation.detailPanel.endPaid'), value: totalsAfterPaid, signed: false },
						{ label: t('common:clientSituation.detailPanel.periodChange'), value: totalsPeriodBalance, signed: true },
					]}
				/>

				<SituationReconciliation
					rows={rows}
					periodActive={periodActive}
					getRowLabel={(rowItem) => entityLabel(rowItem.label)}
					isHighlightedRow={(rowItem) => rowItem.label === ADJUSTED_ROW_LABEL}
					subtitle={`${t('common:clientSituation.detailPanel.entities.adjusted')}: ${t('common:clientSituation.detailPanel.adjustedFormula')}`}
					t={t}
					theme={theme}
				/>

				<SituationChartsGrid
					chartSurface={chartSurface}
					periodActive={periodActive}
					overviewTitle={t('common:clientSituation.detailPanel.charts.endByEntity')}
					overviewChart={(
						<SituationEntityComposedChart
							data={endEntityChartData}
							t={t}
							theme={theme}
							dotRenderer={(props) => {
								const { cx = 0, cy = 0, payload } = props;
								const isAdjustedPoint = payload?.entity === adjustedEntityLabel;
								return (
									<circle
										key={`${payload?.entity}-dot`}
										cx={cx}
										cy={cy}
										r={isAdjustedPoint ? 5 : 4}
										fill={isAdjustedPoint ? theme.palette.primary.main : RECON_CHART_COLORS.balance}
										stroke={isAdjustedPoint ? theme.palette.primary.contrastText : 'none'}
										strokeWidth={isAdjustedPoint ? 1 : 0}
									/>
								);
							}}
						/>
					)}
					periodTitle={t('common:clientSituation.detailPanel.charts.periodActivity')}
					periodChart={(
						<SituationEntityComposedChart
							data={periodEntityChartData}
							t={t}
							theme={theme}
							dotRenderer={(props) => {
								const { cx = 0, cy = 0, payload } = props;
								const isAdjustedPoint = payload?.entity === adjustedEntityLabel;
								return (
									<circle
										key={`${payload?.entity}-dot`}
										cx={cx}
										cy={cy}
										r={isAdjustedPoint ? 5 : 4}
										fill={isAdjustedPoint ? theme.palette.primary.main : RECON_CHART_COLORS.balance}
										stroke={isAdjustedPoint ? theme.palette.primary.contrastText : 'none'}
										strokeWidth={isAdjustedPoint ? 1 : 0}
									/>
								);
							}}
						/>
					)}
					endBalanceTitle={t('common:clientSituation.detailPanel.charts.endBalances')}
					endBalanceChart={(
						<SituationEndBalancesChart data={endBalanceChartData} t={t} theme={theme} />
					)}
					balanceFlowTitle={t('common:clientSituation.detailPanel.charts.balanceFlow')}
					balanceFlowChart={(
						<SituationBalanceFlowChart data={waterfallData} t={t} theme={theme} />
					)}
				/>
			</SituationDetailShell>
		);
	};

	// Define table columns
	const columns = useMemo<MRT_ColumnDef<any>[]>(() => [
		{
			header: t("common:tables.agent"),
			accessorKey: "name",
			Cell: ({ cell }: { cell: any }) => (
				<button
					style={{ background: "none", border: "none", padding: 0, color: "inherit", cursor: "pointer", textDecoration: "underline" }}
					onClick={() => setToView(cell.row.original)}
					disabled={Number(cell.row.original.after_total_balance ?? 0) === 0 || loading}
				>
					{cell.getValue()}
				</button>
			),
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			...creditUsageTableCellProps,
			header: t("common:clientSituation.creditUsage"),
			accessorKey: "after_total_balance",
			Cell: ({ cell }: { cell: any }) => (
				<CreditUsageCell
					balance={Number(cell.row.original.after_total_balance ?? 0)}
					creditLimit={Number(cell.row.original.credit_limit ?? 0)}
					t={t}
					theme={theme}
				/>
			),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
			aggregationFn: 'sum',
			Footer: ({ table }) => {
				const [total, setTotal] = useState<number>(0);

				useEffect(() => {
					setTotal(0);
					const rows = table.getFilteredRowModel().rows;
					const handle = setTimeout(() => {
						let sum = 0;
						for (let i = 0; i < rows.length; i++) {
							const value = Number(rows[i].original.after_total_balance);
							if (Number.isFinite(value)) sum += value;
						}
						setTotal(roundQty(sum));
					}, 0);

					return () => clearTimeout(handle);
				}, [table.getFilteredRowModel().rows]);

				return (
					<Box sx={{ fontWeight: 'bold', color: 'success.main' }}>
						{t("common:report.total")}: {Util.formatNumber(total)}
					</Box>
				);
			},
		},
		{
			muiTableHeadCellProps: { align: 'center' },
			muiTableBodyCellProps: { align: 'center' },
			header: t("common:ui.labels.status"),
			accessorKey: "is_agent",
			Cell: ({ cell }: { cell: any }) => {
				const value = cell.getValue();
				const isActive = value;
				return (
					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
						<Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
							{isActive && (
								<Box
									sx={{
										position: 'absolute',
										width: 10,
										height: 10,
										borderRadius: '50%',
										bgcolor: 'success.main',
										opacity: 0.4,
										animation: 'ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite',
									}}
								/>
							)}
							<Box
								sx={{
									width: 10,
									height: 10,
									borderRadius: '50%',
									bgcolor: isActive ? 'success.main' : 'action.disabled',
									flexShrink: 0,
								}}
							/>
						</Box>
						<Typography
							variant="body2"
							sx={{
								fontWeight: 600,
								color: isActive ? 'success.main' : 'text.disabled',
								letterSpacing: 0.3,
							}}
						>
							{isActive ? t("common:status.active") : t("common:status.inactive")}
						</Typography>
						<style>{`@keyframes ping { 75%, 100% { transform: scale(2.5); opacity: 0; } }`}</style>
					</Box>
				);
			},
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
		}
	], [loading, t, theme]);

	const handleChanges = () => {
		toastIdRef.current = toast.loading(`${t("common:toast.success.changes")} ${t("common:toast.loading.refresh")}`);
		fetchData(false);
	}

	return (
		<>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<TableView
					stateKey={LocalStorageKeys.AGENT_SIT_RECORDS}
					header={t("common:clientSituation.headers.agents")}
					data={data}
					columns={columns}
					includeTimestampColumns={false}
					totals={[{ key: "after_total_balance", label: t("common:tables.owing") }]}
					lastUpdated={lastUpdate}
					period={dateRange}
					setDateRangeFilter={setPeriod}
					isLoading={loading}
					renderDetailPanel={renderAgentDetailPanel}

					renderTopToolbarActions={(table) => (
						<Box sx={{ display: 'flex', gap: '0.5rem' }}>
							<Tooltip title={t("common:ui.tooltips.refreshData")}>
								<IconButton onClick={() => fetchData()} color="primary">
									<Refresh />
								</IconButton>
							</Tooltip>
							<Button
								color="success"
								startIcon={<FileDownload />}
								variant="outlined"
								onClick={() => {
									setExportSelectedIds(
										table.getSelectedRowModel().rows.map((row) => row.original.id),
									);
									setOnExport(true);
								}}
							>
								{t("common:ui.buttons.export")}
							</Button>
						</Box>
					)}

					renderRowActionMenuItems={(row, closeMenu) => [
						Number(row.original.after_total_balance ?? 0) !== 0 && (
							<MenuItem
								key={0}
								disabled={toPay}
								onClick={() => {
									if (allowOrWarn(canRecord(access, 'payment')) || allowOrWarn(canRecord(access, 'refund'))) {
										setToPay({ ...row.original, agent: row.original.name, is_agent: true });
									}
									closeMenu();
								}}
								sx={{
									color: 'success.main',
								}}
							>
								<ListItemIcon sx={{
									color: 'success.main',
								}}>
									<AttachMoney />
								</ListItemIcon>
								{t("common:ui.buttons.pay")}
							</MenuItem>
						),
					]}
				/>

			</LocalizationProvider>

			{/* Invoice Modal */
				toView &&
				<Invoices
					title={t("common:clientSituation.unpaidInvoiceSituationAgent", { name: toView.name })}
					isOpen={!!toView}
					toggle={() => setToView(null)}
					bundle={toView}
					onPayment={handleChanges}
					isAgent={true}
				/>
			}

			{toPay && (
				<BulkPaymentModal
					isOpen={!!toPay}
					toggle={() => setToPay(null)}
					bundle={toPay}
					onSuccess={handleChanges}
				/>
			)}
			<SituationExportDialog
				open={onExport}
				onClose={() => setOnExport(false)}
				mode="agent"
				period={period}
				selectedIds={exportSelectedIds}
				onExportLedger={exportLedger}
				onExportDebt={exportDebt}
			/>
		</>
	);
};

export default AgentSituation;
